import { z } from 'zod';
import { parseChoiceOptions, isValidInvariantNumber } from './adapters/field-type.adapter';
import type { FormField, FormFieldType } from './types';

export const FIELD_TYPE_VALUES = [
  'Text',
  'TextArea',
  'Select',
  'MultiSelect',
  'Email',
  'Number',
  'Date',
  'Time',
  'LinearScale',
  'Grid',
  'Phone',
  'Currency',
] as const satisfies readonly FormFieldType[];

export const fieldTypeSchema = z.enum(FIELD_TYPE_VALUES);

const OPTIONS_REQUIRED_TYPES: FormFieldType[] = ['Select', 'MultiSelect'];

export const formFieldSchema = z
  .object({
    id: z.string().nullable().optional(),
    fieldType: fieldTypeSchema,
    label: z
      .string()
      .min(1, { error: 'forms:builder.validation.labelRequired' })
      .max(500, { error: 'forms:builder.validation.labelTooLong' }),
    required: z.boolean(),
    // 0-based — backend validates `Order >= 0`.
    order: z.number().int().min(0),
    options: z.string().max(2000).nullable().optional(),
    validation: z.string().max(500).nullable().optional(),
  })
  .superRefine((field, ctx) => {
    if (OPTIONS_REQUIRED_TYPES.includes(field.fieldType) && !field.options?.trim()) {
      ctx.addIssue({
        code: 'custom',
        path: ['options'],
        message: 'forms:builder.validation.optionsRequired',
      });
    }
  });

const baseFormObjectSchema = z.object({
  title: z
    .string()
    .min(1, { error: 'forms:builder.validation.titleRequired' })
    .max(200, { error: 'forms:builder.validation.titleTooLong' }),
  description: z.string().max(2000).optional(),
  projectId: z.string().optional(),
  emailRequired: z.boolean(),
  fields: z.array(formFieldSchema).min(1, { error: 'forms:builder.validation.fieldsRequired' }),
});

export const createFormSchema = baseFormObjectSchema;
export type CreateFormInput = z.infer<typeof createFormSchema>;

export const updateFormSchema = baseFormObjectSchema.omit({ projectId: true });
export type UpdateFormInput = z.infer<typeof updateFormSchema>;

export const createTaskFromSubmissionSchema = z.object({
  projectId: z
    .string()
    .min(1, { error: 'forms:submissions.createTask.validation.projectRequired' }),
  boardId: z.string().min(1, { error: 'forms:submissions.createTask.validation.boardRequired' }),
  statusId: z.string().min(1, { error: 'forms:submissions.createTask.validation.statusRequired' }),
  groupId: z.string().optional(),
  assigneeId: z.string().optional(),
});
export type CreateTaskFromSubmissionInput = z.infer<typeof createTaskFromSubmissionSchema>;

/**
 * Submission forms have no static shape — each form defines its own fields at runtime.
 * Built once per `FormField[]` and reused by both the builder preview and the real submit form.
 */
export function buildSubmissionSchema(fields: FormField[], emailRequired: boolean) {
  const shape: Record<string, z.ZodTypeAny> = {};

  for (const field of fields) {
    const key = field.id ?? String(field.order);
    let value: z.ZodTypeAny;
    let choiceOptions: string[] = [];

    switch (field.fieldType) {
      case 'Email':
        // Mirrors the backend's minimal check (contains "@") — not a full RFC format.
        value = z.string().refine((v) => v.includes('@'), {
          error: 'forms:renderer.validation.emailInvalid',
        });
        break;
      case 'Number':
      case 'Currency':
        value = z.string().refine(isValidInvariantNumber, {
          error: 'forms:renderer.validation.numberInvalid',
        });
        break;
      case 'Date':
      case 'Time':
        value = z.string().min(1, { error: 'forms:renderer.validation.dateInvalid' });
        break;
      case 'Select':
        choiceOptions = parseChoiceOptions(field.options);
        value =
          choiceOptions.length > 0 ? z.enum(choiceOptions as [string, ...string[]]) : z.string();
        break;
      case 'MultiSelect':
        choiceOptions = parseChoiceOptions(field.options);
        value = z.array(z.string()).default([]);
        break;
      default:
        value = z.string();
    }

    if (field.required) {
      if (field.fieldType === 'MultiSelect') {
        // `.default([])` above wraps the array in `ZodDefault`, which has no `.min()` — refine
        // achieves the same "at least one selection" check and works on the wrapped type.
        value = value.refine((arr) => Array.isArray(arr) && arr.length > 0, {
          error: 'forms:renderer.validation.required',
        });
      } else if (
        field.fieldType !== 'Number' &&
        field.fieldType !== 'Currency' &&
        field.fieldType !== 'Select'
      ) {
        // Select's required-ness is already implied by z.enum's membership check.
        value = (value as z.ZodString).min(1, { error: 'forms:renderer.validation.required' });
      }
    } else {
      value = value.optional();
    }

    if (field.fieldType === 'MultiSelect' && choiceOptions.length > 0) {
      value = value.refine(
        (arr) => arr === undefined || (arr as string[]).every((v) => choiceOptions.includes(v)),
        { error: 'forms:renderer.validation.multiSelectInvalid' },
      );
    }

    shape[key] = value;
  }

  return z.object({
    email: emailRequired
      ? z.email({ error: 'forms:renderer.validation.emailInvalid' })
      : z.email({ error: 'forms:renderer.validation.emailInvalid' }).optional().or(z.literal('')),
    fields: z.object(shape),
  });
}
export type SubmissionFormInput = z.infer<ReturnType<typeof buildSubmissionSchema>>;

/**
 * Inverse of `buildSubmissionSchema` — turns RHF's `fields` values back into the
 * `{ fieldId, value }[]` wire shape. `MultiSelect` is the only fieldType that produces an
 * array here, and the backend expects it as a JSON-array string, not CSV.
 */
export function mapSubmissionValues(
  fields: Record<string, unknown>,
): { fieldId: string; value: string }[] {
  return Object.entries(fields).map(([fieldId, value]) => ({
    fieldId,
    value: Array.isArray(value) ? JSON.stringify(value) : String(value ?? ''),
  }));
}
